import { useCallback, useEffect, useMemo, useState } from 'react';



import { Schemas } from '@api-client/generated/types';
import { flattenPagedData, flushPagesAfter } from '@utils';


const initialMetadata = {
  currentPage: 1,
  perPage: 0,
  totalPages: 0,
  totalRecords: 0,
};

export const usePagination = <T,>(hash?: unknown) => {
  const [metadata, setMetadata] = useState(initialMetadata);

  const [data, setData] = useState<Record<number, T[]>>({ 1: [] });

  const appendData = useCallback(
    (response: { metadata: Schemas.PaginationMetadata; records: T[] }) => {
      setData((prev) => ({
        ...prev,
        [response.metadata.currentPage]: response.records,
      }));

      setMetadata(response.metadata);
    },
    []
  );

  const reset = useCallback((data: T[] = []) => {
    setMetadata((prev) => ({ ...prev, currentPage: 1 }));
    setData({ 1: data });
  }, []);

  const incrementPage = useCallback(
    () =>
      setMetadata((prev) => ({
        ...prev,
        currentPage: prev.currentPage + 1,
      })),
    []
  );

  const plainData = useMemo(() => flattenPagedData(data), [data]);

  const setPageWithFlush = useCallback((page: number, flushTail?: boolean) => {
    if (flushTail) {
      setData((prev) => flushPagesAfter(prev, page));
    }

    setMetadata((prev) => ({
      ...prev,
      currentPage: page,
    }));
  }, []);

  useEffect(() => {
    reset();
  }, [reset, hash]);

  return {
    metadata,
    incrementPage,
    hasNextPage: metadata.currentPage < metadata.totalPages,
    plainData,
    appendData,
    setPage: setPageWithFlush,
    reset,
    UNSAFE_setData: setData,
  };
};

export default usePagination;